import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Activity, Plus, Check, BarChart3, Signal } from 'lucide-react';
import { watchlistService } from '../services/watchlistService';
import { useLanguage } from '../contexts/LanguageContext';
import FundamentalAnalysis from './FundamentalAnalysis';
import TechnicalAnalysis from './TechnicalAnalysis';

const StockQuote = ({ quote, loading, error, selectedStock }) => {
  const { t } = useLanguage();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [showFundamentalModal, setShowFundamentalModal] = useState(false);
  const [showTechnicalModal, setShowTechnicalModal] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);

  const closeFundamentalModal = useCallback(() => {
    setShowFundamentalModal(false);
  }, []);

  const closeTechnicalModal = useCallback(() => {
    setShowTechnicalModal(false);
  }, []);

  useEffect(() => {
    if (quote && selectedStock) {
      checkWatchlistStatus();
      fetchHistoricalData();
    }
  }, [quote, selectedStock]);

  const fetchHistoricalData = async () => {
    if (!selectedStock) return;
    
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '/api' 
        : 'http://localhost:3001/api';
      
      const response = await fetch(`${API_BASE_URL}/history/${selectedStock.symbol}?period=daily&range=1y`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const historicalArray = Array.isArray(data) ? data : [];
        setHistoricalData(historicalArray);
      } else {
        console.error(`Failed to fetch historical data: ${response.status} ${response.statusText}`);
        setHistoricalData([]);
      }
    } catch (error) {
      console.error('Failed to fetch historical data:', error);
      setHistoricalData([]);
    }
  };

  const checkWatchlistStatus = async () => {
    if (!selectedStock) return;
    try {
      const isInList = await watchlistService.isInWatchlist(selectedStock.symbol);
      setInWatchlist(isInList);
    } catch (error) {
      console.error('Error checking watchlist status:', error);
    }
  };

  const handleAddToWatchlist = async () => {
    if (!selectedStock || inWatchlist) return;
    
    try {
      setWatchlistLoading(true);
      await watchlistService.addToWatchlist(selectedStock);
      setInWatchlist(true);
      // Refresh the watchlist in Portfolio component
      if (window.refreshWatchlist) {
        window.refreshWatchlist();
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    } finally {
      setWatchlistLoading(false);
    }
  };

  const handleRemoveFromWatchlist = async () => {
    if (!selectedStock || !inWatchlist) return;
    
    try {
      setWatchlistLoading(true);
      await watchlistService.removeFromWatchlist(selectedStock.symbol);
      setInWatchlist(false);
      // Refresh the watchlist in Portfolio component
      if (window.refreshWatchlist) {
        window.refreshWatchlist();
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
    } finally {
      setWatchlistLoading(false);
    }
  };
  if (loading) {
    return (
      <div className="card loading-skeleton">
        <div className="skeleton-item" style={{height: '2rem', width: '33%', marginBottom: '1rem'}}></div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem'}}>
          <div className="skeleton-item" style={{height: '4rem'}}></div>
          <div className="skeleton-item" style={{height: '4rem'}}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading quote: {error}</p>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="empty-state">
        <Activity className="empty-state-icon" />
        <p>Select a stock to view its current price</p>
      </div>
    );
  }

  const toNumber = (value, fallback = 0) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
  const formatMoney = (value) => {
    const num = toNumber(value, null);
    return num === null ? '—' : num.toFixed(2);
  };
  const formatVolume = (value) => {
    const num = toNumber(value, null);
    return num === null ? '—' : num.toLocaleString();
  };

  const isPositive = toNumber(quote.change, 0) >= 0;
  const changeIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className="stock-quote">
      <div className="quote-header">
        <div className="quote-info">
          <h2>{quote.symbol}</h2>
          <p className="quote-date">Latest Trading Day: {quote.latestTradingDay}</p>
        </div>
        <div className="quote-actions">
          <div className={`change-indicator ${isPositive ? 'positive' : 'negative'}`}>
            {React.createElement(changeIcon, { size: 16 })}
            <span>{quote.changePercent}</span>
          </div>
          {selectedStock && (
            <div className="mobile-action-buttons">
              <button
                onClick={inWatchlist ? handleRemoveFromWatchlist : handleAddToWatchlist}
                className={`watchlist-btn ${inWatchlist ? 'in-watchlist' : 'not-in-watchlist'}`}
                disabled={watchlistLoading}
                title={inWatchlist ? t('removeFromWatchlist') : t('addToWatchlist')}
              >
                {watchlistLoading ? (
                  <Activity size={16} className="spinning" />
                ) : inWatchlist ? (
                  <Check size={16} />
                ) : (
                  <Plus size={16} />
                )}
                <span>{inWatchlist ? t('inWatchlist') : t('addToWatchlist')}</span>
              </button>
              
              <button
                onClick={() => setShowFundamentalModal(true)}
                className="fundamental-btn"
                title={t('fundamentalAnalysis')}
              >
                <BarChart3 size={16} />
                <span>{t('fundamentalAnalysis')}</span>
              </button>
              
              <button
                onClick={() => setShowTechnicalModal(true)}
                className="technical-btn"
                disabled={!historicalData || historicalData.length === 0}
                title={historicalData && historicalData.length > 0 ? t('technicalAnalysis') : t('loading')}
              >
                <Signal size={16} />
                <span>{t('technicalAnalysis')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric-card blue">
          <div className="metric-header">
            <DollarSign size={20} />
            <span>{t('price')}</span>
          </div>
          <div className="metric-value">${formatMoney(quote.price)}</div>
        </div>

        <div className="metric-card gray">
          <div className="metric-header">
            <span>{t('change')}</span>
          </div>
          <div className="metric-value" style={{color: isPositive ? '#166534' : '#991b1b'}}>
            {isPositive ? '+' : ''}${formatMoney(quote.change)}
          </div>
        </div>

        <div className="metric-card purple">
          <div className="metric-header">
            <span>{t('volume')}</span>
          </div>
          <div className="metric-value">
            {formatVolume(quote.volume)}
          </div>
        </div>

        <div className="metric-card yellow">
          <div className="metric-header">
            <span>{t('previousClose')}</span>
          </div>
          <div className="metric-value">
            ${formatMoney(quote.previousClose)}
          </div>
        </div>
      </div>

      <div className="daily-stats">
        <div className="daily-stat">
          <div className="daily-stat-label">{t('open')}</div>
          <div className="daily-stat-value">${formatMoney(quote.open)}</div>
        </div>
        <div className="daily-stat">
          <div className="daily-stat-label">{t('dayHigh')}</div>
          <div className="daily-stat-value" style={{color: '#166534'}}>${formatMoney(quote.high)}</div>
        </div>
        <div className="daily-stat">
          <div className="daily-stat-label">{t('dayLow')}</div>
          <div className="daily-stat-value" style={{color: '#991b1b'}}>${formatMoney(quote.low)}</div>
        </div>
      </div>
      
      {/* Fundamental Analysis Modal */}
      {showFundamentalModal && selectedStock && (
        <FundamentalAnalysis
          symbol={selectedStock.symbol}
          currentPrice={quote?.price}
          onClose={closeFundamentalModal}
        />
      )}
      
      {/* Technical Analysis Modal */}
      {showTechnicalModal && selectedStock && (
        <TechnicalAnalysis
          symbol={selectedStock.symbol}
          currentPrice={quote?.price}
          historicalData={historicalData}
          onClose={closeTechnicalModal}
        />
      )}
    </div>
  );
};

export default StockQuote;
